import { map, sumBy, filter } from 'lodash';
import React from 'react';
import { Document, Page, Text, View, Image, StyleSheet } from '@react-pdf/renderer';

// Gulf-Pro brand palette (from public/Mark-Color.svg)
const NAVY = '#253e89';
const ORANGE = '#f48d1f';
const INK = '#333333';
const FAINT = '#667085';
const ZEBRA = '#f2f7fb';
const HAIRLINE = '#d9e2ec';

const COLS = { date: 90, invoice: 150, aging: 90, amount: 120, balance: 122 };

// Create styles
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    paddingTop: 24,
    paddingBottom: 84,
    paddingHorizontal: 20,
    fontSize: 9,
    color: INK,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  title: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 22,
    color: NAVY,
    letterSpacing: 2,
  },
  accent: {
    height: 3,
    backgroundColor: ORANGE,
    marginTop: 10,
  },
  label: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 8,
    color: FAINT,
    letterSpacing: 1,
  },
  summaryLine: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingTop: 4,
    paddingBottom: 4,
    borderBottom: 1,
    borderColor: HAIRLINE,
  },
  totalDue: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: NAVY,
    padding: 6,
    marginTop: 6,
  },
  totalDueText: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 10,
    color: 'white',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: NAVY,
  },
  th: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 8,
    color: 'white',
    paddingTop: 5,
    paddingBottom: 5,
    paddingLeft: 8,
    paddingRight: 8,
  },
  row: {
    flexDirection: 'row',
    borderBottom: 1,
    borderColor: HAIRLINE,
  },
  td: {
    fontSize: 9,
    color: INK,
    paddingTop: 4,
    paddingBottom: 4,
    paddingLeft: 8,
    paddingRight: 8,
  },
  sumLabelBox: {
    width: 95,
    backgroundColor: '#eef1f6',
    borderLeft: 1,
    borderTop: 1,
    borderColor: HAIRLINE,
    padding: 4,
    justifyContent: 'center',
  },
  sumLabelText: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 7,
    color: NAVY,
    textAlign: 'center',
  },
  sumValueBox: {
    width: 95,
    borderLeft: 1,
    borderTop: 1,
    borderBottom: 1,
    borderColor: HAIRLINE,
    padding: 5,
  },
  sumValueText: {
    fontSize: 9,
    color: INK,
    textAlign: 'center',
  },
  footer: {
    position: 'absolute',
    bottom: 24,
    left: 20,
    right: 20,
  },
  footerText: {
    fontSize: 8,
    color: FAINT,
    textAlign: 'center',
    marginTop: 2,
  },
});

const withCommas = value => (String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ","));

// Create Document Component
const MyDocument = ({ list, customer, logo = process.env.PUBLIC_URL + '/gp_logo.png' }) => {
    const today = new Date().toLocaleDateString();
    map(list, async (item, index) => {
        const runningTotal = index === 0 ? 0 : Number(list[index -1][10])
        await item.push((Number(item[9]) + runningTotal).toFixed(2));
        return item;
    })
    const format = num => (Number(num).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ","))
    // item[8] is days past due (0 = not yet due), so the buckets line up with
    // the column labels below and with QuickBooks' own aging sections
    const total = format(sumBy(list, i => (Number(i[9]))));
    const current = format(sumBy(filter(list, i => (Number(i[8]) < 1)), i => (Number(i[9]))));
    const thirty = format(sumBy(filter(list, i => (Number(i[8]) > 0 && Number(i[8]) < 31)), i => (Number(i[9]))));
    const sixty = format(sumBy(filter(list, i => (Number(i[8]) > 30 && Number(i[8]) < 61)), i => (Number(i[9]))));
    const ninety = format(sumBy(filter(list, i => (Number(i[8]) > 60 && Number(i[8]) < 91)), i => (Number(i[9]))));
    const old = format(sumBy(filter(list, i => (Number(i[8]) > 90)), i => (Number(i[9]))));
  return (
  <Document title={`Gulf-Pro Services Statement - ${customer}`}>
    <Page size="LETTER" style={styles.page}>
        <Text render={({ pageNumber, totalPages }) => (
                `Page ${pageNumber} of ${totalPages}`
            )}
            fixed
            style={{ fontSize: 8, color: FAINT, position: 'absolute', bottom: 12, right: 20 }}
        />
        <View fixed>
            <View style={styles.headerRow}>
                <View>
                    <Image src={logo} style={{ width: 170, height: 47 }} />
                    <Text style={{ fontSize: 8, color: FAINT, marginTop: 6 }}>P.O. Box 228 · Houma, LA 70361</Text>
                </View>
                <View style={{ alignItems: 'flex-end' }}>
                    <Text style={styles.title}>STATEMENT</Text>
                    <Text style={{ fontSize: 9, color: FAINT, marginTop: 4 }}>{today}</Text>
                </View>
            </View>
            <View style={styles.accent} />
        </View>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 18, marginBottom: 16 }}>
            <View style={{ width: 300 }}>
                <Text style={styles.label}>BILL TO</Text>
                <View style={{ borderBottom: 1, borderColor: HAIRLINE, paddingBottom: 6, marginTop: 4 }}>
                    <Text style={{ fontSize: 11, fontFamily: 'Helvetica-Bold', color: INK }}>{customer}</Text>
                </View>
            </View>
            <View style={{ width: 190 }}>
                <View style={styles.summaryLine}>
                    <Text style={{ fontSize: 9, color: FAINT }}>Statement date</Text>
                    <Text style={{ fontSize: 9, color: INK }}>{today}</Text>
                </View>
                <View style={styles.totalDue}>
                    <Text style={styles.totalDueText}>Total due</Text>
                    <Text style={styles.totalDueText}>${total}</Text>
                </View>
            </View>
        </View>
      <View>
        <View style={styles.tableHeader} fixed>
          <Text style={{ ...styles.th, width: COLS.date }}>DATE</Text>
          <Text style={{ ...styles.th, width: COLS.invoice }}>INVOICE #</Text>
          <Text style={{ ...styles.th, width: COLS.aging, textAlign: 'center' }}>DAYS PAST DUE</Text>
          <Text style={{ ...styles.th, width: COLS.amount, textAlign: 'right' }}>AMOUNT</Text>
          <Text style={{ ...styles.th, width: COLS.balance, textAlign: 'right' }}>BALANCE</Text>
        </View>
        {map(list, (item, index) => (
            <View
                style={index % 2 ? { ...styles.row, backgroundColor: ZEBRA } : styles.row}
                key={item[3]}
                wrap={false}
            >
                <Text style={{ ...styles.td, width: COLS.date }}>{item[1]}</Text>
                <Text style={{ ...styles.td, width: COLS.invoice }}>{item[3]}</Text>
                <Text style={{ ...styles.td, width: COLS.aging, textAlign: 'center' }}>{item[8]}</Text>
                <Text style={{ ...styles.td, width: COLS.amount, textAlign: 'right' }}>{withCommas(item[9])}</Text>
                <Text style={{ ...styles.td, width: COLS.balance, textAlign: 'right' }}>{withCommas(item[10])}</Text>
            </View>
        ))}
      </View>
        <View wrap={false} style={{ marginTop: 18 }}>
            <View style={{ flexDirection: 'row' }}>
                {map(['Current', '1-30 Days Past Due', '31-60 Days Past Due', '61-90 Days Past Due', 'Over 90 Days Past Due', 'Amount Due'], (heading, index) => (
                    <View key={heading} style={styles.sumLabelBox}>
                        <Text style={styles.sumLabelText}>{heading}</Text>
                    </View>
                ))}
                <View style={{ width: 1, backgroundColor: HAIRLINE }} />
            </View>
            <View style={{ flexDirection: 'row' }}>
                {map([current, thirty, sixty, ninety, old], (value, index) => (
                    <View key={index} style={styles.sumValueBox}>
                        <Text style={styles.sumValueText}>{value}</Text>
                    </View>
                ))}
                <View style={styles.sumValueBox}>
                    <Text style={{ ...styles.sumValueText, fontFamily: 'Helvetica-Bold', color: NAVY }}>${total}</Text>
                </View>
                <View style={{ width: 1, backgroundColor: HAIRLINE }} />
            </View>
        </View>
        <View style={styles.footer} fixed>
            <View style={{ height: 1, backgroundColor: HAIRLINE, marginBottom: 4 }} />
            <Text style={styles.footerText}>Please include invoice numbers on your remittance.</Text>
            <Text style={styles.footerText}>Gulf-Pro Services · P.O. Box 228 · Houma, LA 70361</Text>
        </View>
    </Page>
  </Document>
)};

const statementFileName = customer => (
  `${customer.replace(/\./g, '')} ${new Date().toLocaleDateString().replace(/\//g, '-')}.pdf`
);

const saveBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
};

// Mail clients can't receive attachments through mailto: links, so use the
// native share sheet (real attachment) where the browser supports it, and
// otherwise save the PDF and open a prefilled draft to attach it to.
const emailStatement = async (blob, customer, total) => {
  const fileName = statementFileName(customer);
  const subject = `Gulf-Pro Services Statement - ${customer}`;
  const body = `Please find your current statement attached.\r\n\r\nTotal amount due: $${total}\r\n\r\nThank you,\r\nGulf-Pro Services`;
  const file = new File([blob], fileName, { type: 'application/pdf' });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: subject, text: body });
      return;
    } catch (err) {
      if (err.name === 'AbortError') return;
    }
  }
  saveBlob(blob, fileName);
  window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

export { statementFileName, saveBlob, emailStatement };
export default MyDocument;
